import { DsBadge, DsDialog } from '@frontends/ds';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type ClientSession } from '@/types/Auth.ts';
import { SessionCountry } from './SessionCountry';
import { getBrowserName } from './sessionDevice';
import { useSessionFormatters } from './useSessionFormatters';

interface SessionDetailsDialogProps {
  isOpen: boolean;
  onEnd: (session: ClientSession) => void;
  onOpenChange: (isOpen: boolean) => void;
  session: ClientSession | null;
}

const EMPTY_VALUE = '—';

export function SessionDetailsDialog({ session, isOpen, onOpenChange, onEnd }: SessionDetailsDialogProps) {
  const { t } = useTranslation();
  const { getTitle, getCountry, formatDateTime } = useSessionFormatters();

  if (!session) return null;

  const userAgent = session.user_agent;
  const isCurrent = Boolean(session.metadata?.is_current);
  const country = getCountry(session);

  const fields: Array<[string, ReactNode]> = [
    [t('sessions_platform'), userAgent?.platform],
    [t('sessions_OS'), userAgent?.os],
    [t('sessions_browser'), userAgent && getBrowserName(userAgent)],
    [t('sessions_IP'), session.metadata?.ip],
    [
      t('sessions_location'),
      country.code && (
        <>
          <SessionCountry {...country} />
          {country.name && ` (${country.code})`}
        </>
      ),
    ],
    [t('sessions_last_used'), formatDateTime(session.last_used)],
    [t('sessions_created_at'), formatDateTime(session.created_at)],
  ];

  return (
    <DsDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={getTitle(session)}
      titleEnd={
        isCurrent && (
          <DsBadge variant="success" size="sm" showDot>
            {t('sessions_active_now')}
          </DsBadge>
        )
      }
      primaryAction={
        isCurrent
          ? undefined
          : { label: t('sessions_end_session'), variant: 'destructive', onPress: () => onEnd(session) }
      }
      secondaryAction={isCurrent ? undefined : { label: t('close'), onPress: () => onOpenChange(false) }}
    >
      <dl className="flex flex-col gap-3">
        {fields.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-4">
            <dt className="shrink-0 text-textSecondary">{label}</dt>
            <dd className="min-w-0 text-end text-textPrimary wrap-break-word">{value || EMPTY_VALUE}</dd>
          </div>
        ))}
      </dl>
    </DsDialog>
  );
}
